import { id } from '@instantdb/react';
import { db } from './client';

export async function sendMagicCode(email: string) {
  await db.auth.sendMagicCode({ email });
}

export async function verifyMagicCode(email: string, code: string) {
  return db.auth.signInWithMagicCode({ email, code });
}

export async function signOut() {
  try {
    await db.auth.signOut();
  } catch {
    // ignore
  }
}

/**
 * Link any pending memberships (`user` null, `email` matching the signed-in
 * user) to the current user and mark them active. Idempotent.
 */
export async function claimInvites(currentUserId: string, currentUserEmail: string): Promise<number> {
  const lower = currentUserEmail.trim().toLowerCase();

  const { data } = await db.queryOnce({
    memberships: {
      $: { where: { email: lower, status: 'invited' } },
      user: {},
    },
  });

  const pending = (data.memberships ?? []).filter((m) => !m.user);
  if (pending.length === 0) return 0;

  await db.transact(
    pending.flatMap((m) => [
      db.tx.memberships[m.id].update({ status: 'active' }),
      db.tx.memberships[m.id].link({ user: currentUserId }),
    ]),
  );

  return pending.length;
}

export async function createOrganization(
  name: string,
  ownerUserId: string,
  ownerEmail: string,
): Promise<string> {
  const orgId = id();
  const membershipId = id();
  const now = Date.now();

  await db.transact([
    db.tx.organizations[orgId].update({ name, createdAt: now }),
    db.tx.organizations[orgId].link({ owner: ownerUserId }),
    db.tx.memberships[membershipId].update({
      role: 'owner',
      email: ownerEmail.trim().toLowerCase(),
      status: 'active',
      createdAt: now,
    }),
    db.tx.memberships[membershipId].link({ organization: orgId, user: ownerUserId }),
  ]);

  return orgId;
}

export async function inviteMember(orgId: string, email: string, role: 'owner' | 'member' = 'member') {
  const lower = email.trim().toLowerCase();
  const membershipId = id();

  await db.transact([
    db.tx.memberships[membershipId].update({
      role,
      email: lower,
      status: 'invited',
      createdAt: Date.now(),
    }),
    db.tx.memberships[membershipId].link({ organization: orgId }),
  ]);

  return membershipId;
}
